import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Save, RefreshCw, Loader2 } from 'lucide-react';
import { T, CARD, Field, DarkInput, GoldBtn } from './ContentShared';

const API_URL = '';

const ADN_DEFAULTS = {
  hero: { subtitle_fr: 'Développeur & aménageur territorial.', subtitle_en: 'Territorial developer & planner.' },
  ch1: {
    quote_fr: "One Green Developpement imagine et développe des projets profondément ancrés dans leur territoire, où le paysage façonne l'architecture et où le foncier devient un levier stratégique de création de valeur.",
    quote_en: 'One Green Development imagines and develops projects deeply rooted in their territory, where the landscape shapes architecture and land becomes a strategic lever for value creation.',
    body1_fr: "Nous développons des projets qui ne se limitent pas à bâtir, mais qui organisent, structurent et façonnent durablement leur environnement. Notre démarche conjugue innovation, rigueur opérationnelle et exigence qualitative, afin de créer des destinations durables, pensées pour traverser le temps, à l'intersection de l'aménagement intelligent, de l'expérience de vie et d'un investissement responsable et pérenne.",
    body1_en: "We develop projects that go beyond building — they organize, structure and sustainably shape their environment. Our approach combines innovation, operational rigor and qualitative standards, creating lasting destinations designed to stand the test of time, at the intersection of intelligent planning, living experience and responsible, sustainable investment.",
    body2_fr: "Depuis notre création, nous portons une ambition claire : transformer des territoires en lieux de vie organisés, sécurisés et valorisants, au service d'une croissance maîtrisée.",
    body2_en: 'Since our founding, we carry a clear ambition: transforming territories into organized, secure and valuable living spaces, in service of controlled growth.',
  },
  ch2: {
    expertise_title_fr: "L'expertise au service du territoire", expertise_title_en: 'Expertise serving the territory',
    expertise_body1_fr: "Chez One Green Dev, chaque projet naît d'une lecture attentive du territoire. Le relief, la végétation, les flux naturels de Songon ne sont pas des contraintes mais des atouts qui dictent l'orientation des bâtiments, la circulation des espaces et la palette de matériaux.",
    expertise_body1_en: 'At One Green Dev, every project begins with a careful reading of the territory. The terrain, vegetation, and natural flows of Songon are not constraints but assets that dictate building orientation, space flow, and material choices.',
    expertise_body2_fr: "L'aménagement intelligent signifie intégrer les réalités du terrain pour créer des lieux qui respirent, qui s'inscrivent dans le paysage et valorisent chaque mètre carré avec intention.",
    expertise_body2_en: 'Intelligent development means integrating terrain realities to create spaces that breathe, that fit into the landscape and valorize every square meter with purpose.',
    rigor_title_fr: 'Sécurisation foncière & maîtrise du développement', rigor_title_en: 'Land security & development mastery',
    rigor_body1_fr: "One Green Dev place la sécurisation foncière et juridique au plus haut niveau d'exigence. Notre équipe pluridisciplinaire coordonne l'ensemble des intervenants techniques, financiers et institutionnels dans une logique de qualité, de maîtrise des coûts et de respect des délais.",
    rigor_body1_en: 'One Green Dev places land and legal security at the highest level. Our multidisciplinary team coordinates all technical, financial and institutional stakeholders with a focus on quality, cost control and timeline compliance.',
    rigor_body2_fr: "Nous appliquons des standards internationaux à chaque étape, de la conception initiale jusqu'à la livraison, offrant un cadre d'investissement fiable et transparent.",
    rigor_body2_en: 'We apply international standards at every stage, from initial design to delivery, offering a reliable and transparent investment framework.',
  },
  ch3: {
    image_url: 'https://images.unsplash.com/photo-1760327398527-b8593ab5d20d?w=1600&q=80',
    body1_fr: "Première économie de l'Union Économique et Monétaire Ouest Africaine (UEMOA), la Côte d'Ivoire affiche une croissance soutenue et une stabilité qui en font un hub stratégique pour l'investissement en Afrique subsaharienne.",
    body1_en: 'As the leading economy of the West African Economic and Monetary Union (WAEMU), Ivory Coast displays sustained growth and stability, making it a strategic hub for investment in sub-Saharan Africa.',
    body2_fr: "Le Grand Abidjan concentre l'essentiel du dynamisme économique du pays. Songon, situé dans cette zone d'expansion, bénéficie des projets structurants nationaux et d'un potentiel foncier exceptionnel encore largement inexploité.",
    body2_en: 'Greater Abidjan concentrates the country\'s economic dynamism. Songon, located in this expansion zone, benefits from national structural projects and exceptional, largely untapped land potential.',
    closing_fr: "Investir à Songon aujourd'hui, c'est se positionner sur un territoire en pleine structuration, soutenu par des infrastructures nationales majeures et une demande résidentielle croissante.",
    closing_en: 'Investing in Songon today means positioning yourself on a territory in full development, supported by major national infrastructure and growing residential demand.',
  },
  ogd_figures: [
    { value: '341+', label_fr: 'Hectares', label_en: 'Hectares', sub_fr: 'de foncier maîtrisé', sub_en: 'of controlled land' },
    { value: '5', label_fr: 'Projets', label_en: 'Projects', sub_fr: 'en développement', sub_en: 'in development' },
    { value: '15+', label_fr: 'Experts', label_en: 'Experts', sub_fr: 'mobilisés', sub_en: 'mobilized' },
    { value: '100%', label_fr: 'Engagement', label_en: 'Commitment', sub_fr: 'qualité & durabilité', sub_en: 'quality & sustainability' },
  ],
  country_figures: [
    { value: '8%', label_fr: 'Croissance du PIB', label_en: 'GDP Growth', sub_fr: "parmi les plus fortes d'Afrique", sub_en: 'among the strongest in Africa' },
    { value: '#1', label_fr: 'Économie UEMOA', label_en: 'WAEMU Economy', sub_fr: 'première puissance de la zone', sub_en: 'leading economy in the zone' },
    { value: '6M+', label_fr: 'Grand Abidjan', label_en: 'Greater Abidjan', sub_fr: 'habitants — moteur du pays', sub_en: "inhabitants — the country's engine" },
    { value: '2030', label_fr: 'Vision nationale', label_en: 'National Vision', sub_fr: 'plan de développement structurant', sub_en: 'structural development plan' },
  ],
};

const ADN_CHAPTERS = [
  { id: 'hero', title: 'Hero' },
  { id: 'ch1', title: 'Chapitre I — Le Manifeste' },
  { id: 'ch2', title: 'Chapitre II — Héritage' },
  { id: 'ch3', title: 'Chapitre III — La Destination' },
  { id: 'ogd_figures', title: 'Chiffres Clés OGD' },
  { id: 'country_figures', title: 'Chiffres Clés Côte d\'Ivoire' },
];

const ADNEditor = ({ token, showMsg }) => {
  const [adnData, setAdnData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(['ch1']);

  useEffect(() => {
    const fetchAdn = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/adn-content`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const { content } = await res.json();
          if (content) {
            const merged = { ...ADN_DEFAULTS };
            for (const key of Object.keys(ADN_DEFAULTS)) {
              if (Array.isArray(ADN_DEFAULTS[key])) {
                merged[key] = content[key] || ADN_DEFAULTS[key];
              } else if (typeof ADN_DEFAULTS[key] === 'object') {
                merged[key] = { ...ADN_DEFAULTS[key], ...(content[key] || {}) };
              }
            }
            setAdnData(merged);
          } else {
            setAdnData(ADN_DEFAULTS);
          }
        } else {
          setAdnData(ADN_DEFAULTS);
        }
      } catch (err) { console.error('Failed to load ADN content:', err); setAdnData(ADN_DEFAULTS); }
      setLoading(false);
    };
    fetchAdn();
  }, [token]);

  const saveAdn = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/adn-content`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(adnData),
      });
      showMsg(res.ok ? 'success' : 'error', res.ok ? 'Contenu ADN sauvegardé' : 'Erreur');
    } catch (err) { console.error('Failed to save ADN content:', err); showMsg('error', 'Erreur de connexion'); }
    setSaving(false);
  };

  const update = (chapter, field, value) => {
    setAdnData(prev => ({ ...prev, [chapter]: { ...prev[chapter], [field]: value } }));
  };

  const updateFigure = (key, idx, field, value) => {
    setAdnData(prev => {
      const figs = [...(prev[key] || [])];
      figs[idx] = { ...figs[idx], [field]: value };
      return { ...prev, [key]: figs };
    });
  };

  const toggle = (id) => setExpanded(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  if (loading) return <div className="flex items-center justify-center h-48"><Loader2 className="w-5 h-5 animate-spin" style={{ color: T.vert }} /></div>;

  const renderTextFields = (chapter, fields) => (
    <div className="space-y-4">
      {fields.map(([field, label, rows]) => (
        <div key={field} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={`${label} (FR)`}>
            <DarkInput value={adnData?.[chapter]?.[field + '_fr'] || ''} onChange={e => update(chapter, field + '_fr', e.target.value)} rows={rows || 2} />
          </Field>
          <Field label={`${label} (EN)`}>
            <DarkInput value={adnData?.[chapter]?.[field + '_en'] || ''} onChange={e => update(chapter, field + '_en', e.target.value)} rows={rows || 2} />
          </Field>
        </div>
      ))}
    </div>
  );

  const renderFigures = (key) => (
    <div className="space-y-4">
      {(adnData?.[key] || []).map((fig, i) => (
        <div key={`fig-${key}-${i}-${fig.value}`} className="rounded-xl p-4 space-y-3" style={{ background: '#f5f3ef', border: `1px solid ${T.border}` }}>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold" style={{ color: T.or }}>#{i + 1}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Valeur"><DarkInput value={fig.value} onChange={e => updateFigure(key, i, 'value', e.target.value)} /></Field>
            <Field label="Label (FR)"><DarkInput value={fig.label_fr} onChange={e => updateFigure(key, i, 'label_fr', e.target.value)} /></Field>
            <Field label="Label (EN)"><DarkInput value={fig.label_en} onChange={e => updateFigure(key, i, 'label_en', e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Sous-texte (FR)"><DarkInput value={fig.sub_fr} onChange={e => updateFigure(key, i, 'sub_fr', e.target.value)} /></Field>
            <Field label="Sous-texte (EN)"><DarkInput value={fig.sub_en} onChange={e => updateFigure(key, i, 'sub_en', e.target.value)} /></Field>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="adn-editor">
      {ADN_CHAPTERS.map(ch => (
        <div key={ch.id} className="rounded-2xl overflow-hidden" style={CARD}>
          <button onClick={() => toggle(ch.id)} className="w-full flex items-center justify-between p-5" data-testid={`adn-section-${ch.id}`}>
            <span className="text-sm font-semibold" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>{ch.title}</span>
            {expanded.includes(ch.id) ? <ChevronUp className="w-4 h-4" style={{ color: T.textMuted }} /> : <ChevronDown className="w-4 h-4" style={{ color: T.textMuted }} />}
          </button>
          {expanded.includes(ch.id) && (
            <div className="px-5 pb-5" style={{ borderTop: `1px solid ${T.border}` }}>
              {ch.id === 'hero' && renderTextFields('hero', [['subtitle', 'Sous-titre Hero']])}
              {ch.id === 'ch1' && renderTextFields('ch1', [
                ['quote', 'Citation principale', 3],
                ['body1', 'Paragraphe 1', 3],
                ['body2', 'Paragraphe 2', 3],
              ])}
              {ch.id === 'ch2' && renderTextFields('ch2', [
                ['expertise_title', 'Titre Expertise'],
                ['expertise_body1', 'Expertise — Paragraphe 1', 3],
                ['expertise_body2', 'Expertise — Paragraphe 2', 3],
                ['rigor_title', 'Titre Rigueur'],
                ['rigor_body1', 'Rigueur — Paragraphe 1', 3],
                ['rigor_body2', 'Rigueur — Paragraphe 2', 3],
              ])}
              {ch.id === 'ch3' && (
                <div className="space-y-4">
                  <Field label="Image du Chapitre III (URL)">
                    <DarkInput value={adnData?.ch3?.image_url || ''} onChange={e => update('ch3', 'image_url', e.target.value)} placeholder="https://..." testId="ch3-image-url" />
                    {adnData?.ch3?.image_url && <img src={adnData.ch3.image_url} alt="" className="mt-2 h-32 w-full object-cover rounded-xl opacity-80" />}
                  </Field>
                  {renderTextFields('ch3', [
                    ['body1', 'Paragraphe 1', 3],
                    ['body2', 'Paragraphe 2', 3],
                    ['closing', 'Phrase de conclusion', 3],
                  ])}
                </div>
              )}
              {ch.id === 'ogd_figures' && renderFigures('ogd_figures')}
              {ch.id === 'country_figures' && renderFigures('country_figures')}
            </div>
          )}
        </div>
      ))}

      <div className="flex justify-end pt-2">
        <GoldBtn onClick={saveAdn} disabled={saving} testId="save-adn-btn">
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer ADN OGD
        </GoldBtn>
      </div>
    </div>
  );
};

export default ADNEditor;
